import { lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { MasterLayout } from "../_metronic/layout/MasterLayout";
import { NewMasterLayout } from "../app/newLayout/NewLayout";
import { SuspensedView } from "../components/organisms/ErrorBoundary";
import { DashboardWrapper } from "../pages/dashboard/DashboardWrapper";
import { HomeRedirect } from "./HomeRedirect";

const AccountPage = lazy(() => import("../app/account/Page"));
const BillPage = lazy(() => import("../app/bill/Page"));
const Bulk = lazy(() => import("../app/bulk/index"));
const ChatPage = lazy(() => import("../app/chat/Page"));
const CompliancePage = lazy(() => import("../app/compliance/Page"));
const FileManagerPage = lazy(() => import("../app/fileManager/Page"));
const IntegrationPage = lazy(() => import("../app/integrationV1/routes"));
const InventoryPage = lazy(() => import("../app/newLayout/page/Inventory/InventoryPage"));
const AllocationPage = lazy(() => import("../app/newLayout/page/Allocation/Allocation"));
const InvoicePage = lazy(() => import("../app/invoice/Page"));
const Master = lazy(() => import("../app/master/index"));
const OrderPage = lazy(() => import("../app/order/Page"));
const PlannerPage = lazy(() => import("../app/planner/Page"));
const PlannedOrderLayout = lazy(() =>
  import("../app/planner/plannedOrder/layout/list").then(m => ({ default: m.PlannedOrderLayout })),
);
const PurchaseOrderRoutes = lazy(() => import("../app/purchaseOrder/routes"));
const ReceiptPage = lazy(() => import("../app/newLayout/page/Receipt"));
const ReportsPage = lazy(() => import("../app/newLayout/page/Reports/ReportsPage"));
const ShipmentPage = lazy(() => import("../app/shipment/Page"));
const SupplierPage = lazy(() => import("../app/supplier/Page"));
const TransferOrder = lazy(() => import("../app/order/transferV2/Page"));
const TransferOrderList = lazy(() => import("../app/order/transferV2/TransferOrder"));
const UserPage = lazy(() => import("../app/user/Page"));
const CreateReceiptBill = lazy(() => import("../app/receipt/bill/createReceiptBill"));
const SupplierCreditNoteLayout = lazy(() => import("../app/supplierCreditNote/layout/list"));
const SupplierCreditNote = lazy(() => import("../app/supplierCreditNote/routes"));
const CustomerCreditNoteLayout = lazy(() => import("../app/customerCreditNote/layout/list"));
const CustomerCreditNoteRoutes = lazy(() => import("../app/customerCreditNote/routes"));
const PromoManage = lazy(() => import("../app/promo/routes"));
const PromoList = lazy(() => import("../app/promo/layout/list"));
const PicklistRoutes = lazy(() => import("../app/picklist/routes"));
const PicklistLayout = lazy(() => import("../app/picklist/layout/list"));
const ReceiptAdjustmentPage = lazy(() => import("../app/receiptAdjustment/Page"));
const PrepaymentPage = lazy(() => import("../app/prepayment/page"));
const CustomerPaymentsPage = lazy(() =>
  import("../app/saleInvoice/customerPayments/CustomerPaymentsPage"),
);
const OrganizationPage = lazy(() => import("../app/organization/Page"));
const ReplenishmentPage = lazy(() => import("../app/replenishment/page"));
const PricelistRoutes = lazy(() => import("../app/priceList/routes"));
const QuotationRoutes = lazy(() => import("../app/quotation/routes"));
const SalesOrderRoutes = lazy(() => import("../app/salesOrder/routes"));
const StockPackRoutes = lazy(() => import("../app/stockPack/routes"));
const DeliveryRoutes = lazy(() => import("../app/delivery/routes"));
const ShipmentPageV1 = lazy(() => import("../app/shipmentV1/routes"));
const CustomerReturnRoutes = lazy(() => import("../app/customerReturn/routes"));
const SaleInvoiceRoutes = lazy(() => import("../app/saleInvoice/Page"));
const PortalRoutes = lazy(() => import("../app/b2bPortal/routes"));
const PortalHomePage = lazy(() => import("../app/b2bPortal/portal"));
const StandardBomRoutes = lazy(() => import("../app/standardBom/routes"));
const ManufacturingOrderRoutes = lazy(() => import("../app/manufacturingOrder/routes"));
const CoManufacturingOrderRoutes = lazy(() => import("../app/coManufacturingOrder/routes"));

const PrivateRoutes = () => {
  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Pages */}
        {/* Lazy Apps */}
        <Route
          path="chat/*"
          element={
            <SuspensedView moduleName="Chat">
              <ChatPage />
            </SuspensedView>
          }
        />

        <Route
          path="bill/*"
          element={
            <SuspensedView moduleName="Bill">
              <BillPage />
            </SuspensedView>
          }
        />

        <Route
          path="compliance/*"
          element={
            <SuspensedView moduleName="Compliance">
              <CompliancePage />
            </SuspensedView>
          }
        />

        <Route
          path="invoices/*"
          element={
            <SuspensedView moduleName="Invoices">
              <InvoicePage />
            </SuspensedView>
          }
        />

        <Route
          path="orders/*"
          element={
            <SuspensedView moduleName="Orders">
              <OrderPage />
            </SuspensedView>
          }
        />

        <Route
          path="shipments/*"
          element={
            <SuspensedView moduleName="Shipments">
              <ShipmentPage />
            </SuspensedView>
          }
        />

        <Route
          path="supplier-metrics/*"
          element={
            <SuspensedView moduleName="Supplier Metrics">
              <SupplierPage />
            </SuspensedView>
          }
        />

        <Route
          path="file-manager/*"
          element={
            <SuspensedView moduleName="File Manager">
              <FileManagerPage />
            </SuspensedView>
          }
        />

        <Route path="*" element={<HomeRedirect />} />
      </Route>

      <Route element={<NewMasterLayout />}>
        <Route
          path="users/*"
          element={
            <SuspensedView moduleName="Users">
              <UserPage />
            </SuspensedView>
          }
        />
        {/* Pages */}
        <Route path="dashboard" element={<DashboardWrapper />} />

        <Route
          path="planner/planned_order/*"
          element={
            <SuspensedView moduleName="Planned Orders">
              <PlannedOrderLayout />
            </SuspensedView>
          }
        />
        <Route
          path="planner/projected_stock/*"
          element={
            <SuspensedView moduleName="Projected Stocks">
              <PlannerPage />
            </SuspensedView>
          }
        />
        <Route path="planner" element={<Navigate to="/planner/planned_order" replace />} />

        <Route
          path="bulk/*"
          element={
            <SuspensedView moduleName="Bulk">
              <Bulk />
            </SuspensedView>
          }
        />

        <Route
          path="integration/*"
          element={
            <SuspensedView moduleName="Integration">
              <IntegrationPage />
            </SuspensedView>
          }
        />

        <Route
          path="shipment/*"
          element={
            <SuspensedView moduleName={"Shipment"}>
              <ShipmentPageV1 />
            </SuspensedView>
          }
        />

        <Route
          path="inventory/*"
          element={
            <SuspensedView moduleName="Inventory">
              <InventoryPage />
            </SuspensedView>
          }
        />

        <Route
          path="orders/allocation/*"
          element={
            <SuspensedView moduleName="Allocation">
              <AllocationPage />
            </SuspensedView>
          }
        />

        <Route
          path="reports/*"
          element={
            <SuspensedView moduleName="Reports">
              <ReportsPage />
            </SuspensedView>
          }
        />

        <Route
          path="orders/purchase/*"
          element={
            <SuspensedView moduleName="Purchase Order">
              <PurchaseOrderRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="transfer_order/*"
          element={
            <SuspensedView moduleName="Transfer Order">
              <TransferOrder />
            </SuspensedView>
          }
        />

        <Route
          path="transfer_orders"
          element={
            <SuspensedView moduleName="Transfer Orders">
              <TransferOrderList />
            </SuspensedView>
          }
        />

        <Route
          path="receipts/*"
          element={
            <SuspensedView moduleName="Receipts">
              <ReceiptPage />
            </SuspensedView>
          }
        />

        <Route
          path="receipt_adjustment/*"
          element={
            <SuspensedView moduleName="Receipt Adjustment">
              <ReceiptAdjustmentPage />
            </SuspensedView>
          }
        />

        <Route
          path="invoice/create_from_receipt/"
          element={
            <SuspensedView moduleName="Create Invoice from Receipt">
              <CreateReceiptBill />
            </SuspensedView>
          }
        />

        <Route
          path="bills/*"
          element={
            <SuspensedView moduleName="Bills">
              <BillPage />
            </SuspensedView>
          }
        />

        <Route path="prepayments/*" element={<Navigate to="/payments" replace />} />

        <Route
          path="payments/*"
          element={
            <SuspensedView moduleName="Payments">
              <PrepaymentPage />
            </SuspensedView>
          }
        />

        <Route
          path="customer-payments"
          element={
            <SuspensedView moduleName="Customer payments">
              <CustomerPaymentsPage />
            </SuspensedView>
          }
        />

        <Route
          path="promos"
          element={
            <SuspensedView moduleName="Promos">
              <PromoList />
            </SuspensedView>
          }
        />

        <Route
          path="promo/*"
          element={
            <SuspensedView moduleName="Promo">
              <PromoManage />
            </SuspensedView>
          }
        />

        <Route
          path="supplier_credit_notes"
          element={
            <SuspensedView moduleName="Supplier Credit Notes">
              <SupplierCreditNoteLayout />
            </SuspensedView>
          }
        />

        <Route
          path="supplier_credit_note/*"
          element={
            <SuspensedView moduleName="Supplier Credit Note">
              <SupplierCreditNote />
            </SuspensedView>
          }
        />

        <Route
          path="customer_credit_notes"
          element={
            <SuspensedView moduleName="Customer Credit Notes">
              <CustomerCreditNoteLayout />
            </SuspensedView>
          }
        />

        <Route
          path="customer_credit_note/*"
          element={
            <SuspensedView moduleName="Customer Credit Note">
              <CustomerCreditNoteRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="account/*"
          element={
            <SuspensedView moduleName="Account">
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path="organization/*"
          element={
            <SuspensedView moduleName="Organization">
              <OrganizationPage />
            </SuspensedView>
          }
        />
        <Route
          path="replenishments/*"
          element={
            <SuspensedView moduleName="Replenishments">
              <ReplenishmentPage />
            </SuspensedView>
          }
        />

        <Route
          path="picklists/*"
          element={
            <SuspensedView moduleName="Picklists">
              <PicklistLayout />
            </SuspensedView>
          }
        />

        <Route
          path="picklist/*"
          element={
            <SuspensedView moduleName="Picklist">
              <PicklistRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="master/*"
          element={
            <SuspensedView moduleName="Master">
              <Master />
            </SuspensedView>
          }
        />

        <Route
          path="sales/pricelist/*"
          element={
            <SuspensedView moduleName="Price List">
              <PricelistRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="sales/quotation/*"
          element={
            <SuspensedView moduleName="Quotation">
              <QuotationRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="stock_packs/*"
          element={
            <SuspensedView moduleName="Stock Packs">
              <StockPackRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="stock_packs/*"
          element={
            <SuspensedView moduleName="Stock Packs">
              <StockPackRoutes />
            </SuspensedView>
          }
        />
        <Route
          path="delivery/*"
          element={
            <SuspensedView moduleName="Delivery">
              <DeliveryRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="sales/order/*"
          element={
            <SuspensedView moduleName="Sales Order">
              <SalesOrderRoutes />
            </SuspensedView>
          }
        />
        <Route
          path="customer_return/*"
          element={
            <SuspensedView>
              <CustomerReturnRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="sale_invoice/*"
          element={
            <SuspensedView>
              <SaleInvoiceRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="config_portals"
          element={
            <SuspensedView moduleName="Portals">
              <PortalHomePage />
            </SuspensedView>
          }
        />

        <Route
          path="config_portal/*"
          element={
            <SuspensedView moduleName="Portal">
              <PortalRoutes />
            </SuspensedView>
          }
        />

        <Route
          path="bom/*"
          element={
            <SuspensedView moduleName="Bills of materials">
              <StandardBomRoutes />
            </SuspensedView>
          }
        />
        <Route
          path="manufacturing-order/*"
          element={
            <SuspensedView moduleName="Manufacturing Orders">
              <ManufacturingOrderRoutes />
            </SuspensedView>
          }
        />
        <Route
          path="co-manufacturing-order/*"
          element={
            <SuspensedView moduleName="Co manufacturing Orders">
              <CoManufacturingOrderRoutes />
            </SuspensedView>
          }
        />

        <Route path="*" element={<HomeRedirect />} />
      </Route>
    </Routes>
  );
};

export { PrivateRoutes };
